import { type QueryClient } from '@tanstack/react-query'

import {
  type DispatchListDetailExEntity,
  type DispatchListExEntity,
  type DispatchListWithOrderExEntity
} from '@/libs/api'
import { queryClient } from '@/libs/react-query/query-client'

import { type FetchDispatchesQuery } from '../api'
import { type FetchVehicleDispatchBoardsResponse } from '../api/fetch-vehicle-dispatch-boards'
import { getDispatchesQueryOptions } from '../query-options/get-dispatches-query-options'

import {
  invalidateNewBoardCache,
  newBoardQueryKey
} from './board-cache-invalidation'
import { shouldInvalidateShipmentOrdersQueryKey } from './shipment-order-query-key'

/**
 * アクティブな配車ボードキャッシュからデータを取得する。
 * invalidateNewBoardCache() の await 後に呼ぶことで、
 * リフェッチ済みの最新データを1回のAPIコールで参照できる。
 */
export const getActiveBoardData = () => {
  const entries =
    queryClient.getQueriesData<FetchVehicleDispatchBoardsResponse>({
      queryKey: newBoardQueryKey
    })
  return entries[0]?.[1]?.data ?? null
}

export type BoardCache = { data: DispatchListWithOrderExEntity }

const sortDetailsAsc = (details: DispatchListDetailExEntity[]) => {
  return [...details].sort((a, b) => {
    const at = new Date(a.startAt).getTime()
    const bt = new Date(b.startAt).getTime()
    if (at !== bt) return at - bt
    return a.id - b.id
  })
}

export const findDetail = (
  cache: BoardCache,
  detailId: number
): { dispatchIdx: number; detailIdx: number } | null => {
  const { dispatches } = cache.data
  for (let i = 0; i < dispatches.length; i++) {
    const di = dispatches[i].details.findIndex((d) => d.id === detailId)
    if (di >= 0) return { dispatchIdx: i, detailIdx: di }
  }
  return null
}

/** 旧キャッシュからdetailを除去する（invalidateなし） */
export const removeDetailFromBoardCacheOnly = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  detailId: number
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old
    const loc = findDetail(old, detailId)
    if (!loc) return old
    const next: BoardCache = {
      data: {
        ...old.data,
        dispatches: old.data.dispatches.map((dispatch, idx) => {
          if (idx !== loc.dispatchIdx) return dispatch
          return {
            ...dispatch,
            details: dispatch.details.filter((d) => d.id !== detailId)
          }
        })
      }
    }
    return next
  })
}

export const removeDetailFromBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  detailId: number
) => {
  removeDetailFromBoardCacheOnly(queryClient, query, detailId)
  invalidateNewBoardCache()
}

export const upsertDetailIntoBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  detail: DispatchListDetailExEntity
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old
    const { dispatches } = old.data
    const targetIdx = dispatches.findIndex((d) => d.id === detail.dispatchId)
    if (targetIdx < 0) return old

    const target = dispatches[targetIdx]
    const existed = target.details.some((d) => d.id === detail.id)
    const updatedDetails = existed
      ? target.details.map((d) => (d.id === detail.id ? detail : d))
      : [...target.details, detail]

    const next: BoardCache = {
      data: {
        ...old.data,
        dispatches: dispatches.map((d, idx) =>
          idx === targetIdx
            ? { ...d, details: sortDetailsAsc(updatedDetails) }
            : d
        )
      }
    }
    return next
  })
  invalidateNewBoardCache()
}

export const upsertDetailsIntoBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  details: DispatchListDetailExEntity[]
) => {
  for (const d of details) {
    upsertDetailIntoBoardCache(queryClient, query, d)
  }
}

export const moveDetailOnBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  detailId: number,
  toDispatchId: number,
  patch: Partial<Pick<DispatchListDetailExEntity, 'startAt' | 'endAt'>>
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old
    const loc = findDetail(old, detailId)
    if (!loc) return old

    const fromDispatch = old.data.dispatches[loc.dispatchIdx]
    const current = fromDispatch.details[loc.detailIdx]
    const moved: DispatchListDetailExEntity = {
      ...current,
      dispatchId: toDispatchId,
      startAt: patch.startAt ?? current.startAt,
      endAt: patch.endAt ?? current.endAt
    }

    const dispatches = old.data.dispatches.map((dispatch, idx) => {
      if (idx === loc.dispatchIdx) {
        // remove from source
        return {
          ...dispatch,
          details: dispatch.details.filter((d) => d.id !== detailId)
        }
      }
      if (dispatch.id === toDispatchId) {
        // insert to target
        const details = sortDetailsAsc([...dispatch.details, moved])
        return { ...dispatch, details }
      }
      return dispatch
    })

    return { data: { ...old.data, dispatches } }
  })
  invalidateNewBoardCache()
}

/**
 * サーバ返却の明細データでボードキャッシュを更新する
 * - dispatch 移動を含む（旧 dispatch から削除 → 新 dispatch へ差し替え）
 * - 詳細のソート順を維持
 */
export const updateDetailOnBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  newDetail: DispatchListDetailExEntity
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old

    const loc = findDetail(old, newDetail.id)
    const targetDispatchIdx = old.data.dispatches.findIndex(
      (d) => d.id === newDetail.dispatchId
    )
    if (targetDispatchIdx < 0) return old

    // 旧dispatchからの削除 + 新dispatchへ upsert
    const dispatches = old.data.dispatches.map((dispatch, idx) => {
      // 旧dispatchに存在した場合は削除
      if (loc && idx === loc.dispatchIdx) {
        return {
          ...dispatch,
          details: dispatch.details.filter((d) => d.id !== newDetail.id)
        }
      }
      return dispatch
    })

    // 新dispatchへ upsert
    const currentTarget = dispatches[targetDispatchIdx]
    const existed = currentTarget.details.some((d) => d.id === newDetail.id)
    const updatedDetails = existed
      ? currentTarget.details.map((d) =>
          d.id === newDetail.id ? newDetail : d
        )
      : [...currentTarget.details, newDetail]

    dispatches[targetDispatchIdx] = {
      ...currentTarget,
      details: sortDetailsAsc(updatedDetails)
    }

    return { data: { ...old.data, dispatches } }
  })
}

/**
 * サーバ返却の運行データで、該当行（dispatch）を差し替え
 * - detailsは返却に含まれている想定（含まれない場合は既存を保持したいが、API仕様に合わせて完全差し替え）
 */
export const updateDispatchOnBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  newDispatch: DispatchListExEntity
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old
    const idx = old.data.dispatches.findIndex((d) => d.id === newDispatch.id)
    if (idx < 0) return old
    const dispatches = old.data.dispatches.slice()
    dispatches[idx] = newDispatch
    return { data: { ...old.data, dispatches } }
  })
  invalidateNewBoardCache()
}

export const updateOrdersOnBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  orders: {
    regularTruckOrder?: number[]
    notRegularTruckDispatchOrder?: number[]
  }
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old
    const next: BoardCache = {
      data: {
        ...old.data,
        regularTruckOrder:
          orders.regularTruckOrder ?? old.data.regularTruckOrder,
        notRegularTruckDispatchOrder:
          orders.notRegularTruckDispatchOrder ??
          old.data.notRegularTruckDispatchOrder
      }
    }
    return next
  })
  invalidateNewBoardCache()
}

export const removeDispatchFromBoardCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  dispatchId: number
) => {
  const key = getDispatchesQueryOptions(query).queryKey
  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old

    // 削除対象のdispatchを見つける
    const targetDispatch = old.data.dispatches.find((d) => d.id === dispatchId)
    if (!targetDispatch) return old

    // dispatchesから削除
    const nextDispatches = old.data.dispatches.filter(
      (d) => d.id !== dispatchId
    )

    // regularTruckOrder / notRegularTruckDispatchOrder からも削除
    let nextRegularTruckOrder = old.data.regularTruckOrder
    let nextNotRegularTruckDispatchOrder = old.data.notRegularTruckDispatchOrder

    if (
      targetDispatch.truckId !== null &&
      targetDispatch.truck?.usageType === 'REGULAR'
    ) {
      nextRegularTruckOrder = old.data.regularTruckOrder?.filter(
        (truckId) => truckId !== targetDispatch.truckId
      )
    } else {
      nextNotRegularTruckDispatchOrder =
        old.data.notRegularTruckDispatchOrder?.filter((id) => id !== dispatchId)
    }

    const next: BoardCache = {
      data: {
        ...old.data,
        dispatches: nextDispatches,
        regularTruckOrder: nextRegularTruckOrder,
        notRegularTruckDispatchOrder: nextNotRegularTruckDispatchOrder
      }
    }
    return next
  })
  invalidateNewBoardCache()
}

/**
 * 配車板キャッシュ内の配車データを更新または追加する
 *
 * サーバーから取得した最新の配車データを既存のキャッシュにマージします。
 * - 既存の配車（ID一致）: データを上書き更新
 * - 新規の配車（ID不一致）: 配列末尾に追加
 *
 * @example
 * // mutation成功後、部分取得したデータでキャッシュを更新
 * const { data } = await queryClient.fetchQuery(
 *   getDispatchesQueryOptions({
 *     tenantKey,
 *     query: { shippingDate, dcId, dispatchIds: [123], daysToFetch: 3 }
 *   })
 * )
 *
 * // data.dispatches = [{ id: 123, details: [...新しいデータ] }]
 *
 * updateDispatchesInCache(
 *   queryClient,
 *   { tenantKey, query: { shippingDate, dcId, daysToFetch: 3 } },
 *   data.dispatches
 * )
 *
 * // キャッシュ更新前: dispatches = [{ id: 100, ... }, { id: 123, details: [...古い] }, { id: 200, ... }]
 * // キャッシュ更新後: dispatches = [{ id: 100, ... }, { id: 123, details: [...新しい] }, { id: 200, ... }]
 */
export const updateDispatchesInCache = (
  queryClient: QueryClient,
  query: FetchDispatchesQuery,
  newDispatches: DispatchListExEntity[]
) => {
  // queryKeyを生成
  const key = getDispatchesQueryOptions(query).queryKey

  queryClient.setQueryData(key, (old: BoardCache | undefined) => {
    if (!old) return old

    // 既存の配車データをコピー
    const nextDispatches = [...old.data.dispatches]

    // 新しいデータを既存データにマージ
    newDispatches.forEach((newDispatch) => {
      const index = nextDispatches.findIndex((d) => d.id === newDispatch.id)
      if (index >= 0) {
        // 既存の配車を更新（上書き）
        nextDispatches[index] = newDispatch
      } else {
        // 新規の配車を追加
        nextDispatches.push(newDispatch)
      }
    })

    // 更新されたキャッシュを返す
    return {
      ...old,
      data: {
        ...old.data,
        dispatches: nextDispatches
      }
    }
  })
  invalidateNewBoardCache()
}

export const invalidateShipmentOrders = (params: {
  tenantKey: string
  shippingDate: string
}) => {
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      shouldInvalidateShipmentOrdersQueryKey(queryKey, params)
  })
}
